import React, { useEffect, useState } from "react";
import { Pencil, Save, X } from "lucide-react";
import { reApi, errMsg, fmtDate, pretty } from "@/lib/residentApi";
import { inputCls, labelCls, GoldButton, GhostButton, ErrorLine } from "./ui";
import HousingSelect from "./HousingSelect";
import { IntakeDetails } from "./IntakeDetails";
import { LoginPinRow } from "./LoginPinRow";
import { PartnersRow } from "./PartnersRow";
import { SsnRow } from "./SsnRow";
import { useAuth } from "@/lib/auth";

const GOV = [["ss_card", "Social Security Card"], ["birth_certificate", "Birth Certificate"], ["state_id", "State ID"], ["dd214", "DD-214"]];
const GOV_STATUS = ["missing", "in_progress", "recovered"];

export const Row = ({ label, children, testId }) => (
  <div className="p-3 border border-white/5 rounded-sm bg-white/[0.02]">
    <div className={labelCls}>{label}</div>
    <div data-testid={testId} className="text-nex-silver text-sm">{children || "—"}</div>
  </div>
);

const toForm = (r) => ({
  first_name: r.first_name, last_name: r.last_name, preferred_name: r.preferred_name || "",
  status: r.status, intake_date: r.intake_date || "",
  case_manager: { name: r.case_manager?.name || "", email: r.case_manager?.email || "" },
  housing: r.housing || { property_id: null, room_id: null, bed_id: null },
  gov: Object.fromEntries(GOV.map(([k]) => [k, r.government_ids?.[k] || "missing"])), ssn: "",
});

function ProfileView({ resident, housingLabel, sa }) {
  const g = resident.government_ids || {};
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <Row label="Move-in / intake date" testId="profile-intake-date">{fmtDate(resident.intake_date)}</Row>
        {(resident.added_by || resident.created_by) && <Row label="Added by" testId="profile-added-by">{[(resident.added_by || resident.created_by)?.name, resident.added_by?.role].filter(Boolean).join(" · ")}</Row>}
        <Row label="Case manager" testId="profile-case-manager">{[resident.case_manager?.name, resident.case_manager?.email].filter(Boolean).join(" · ")}</Row>
        <Row label="Housing (unit · room · bed)" testId="profile-housing">{housingLabel}</Row>
        <SsnRow resident={resident} sa={sa} />
        <LoginPinRow resident={resident} />
        <PartnersRow resident={resident} />
      </div>
      <div>
        <div className={labelCls}>Government IDs</div>
        <div className="grid sm:grid-cols-4 gap-2">
          {GOV.map(([k, l]) => <Row key={k} label={l} testId={`profile-gov-${k}`}>{pretty(g[k])}</Row>)}
        </div>
      </div>
      {!!resident.legacy_details?.length && (
        <div data-testid="profile-legacy-details">
          <div className={labelCls}>Legacy details · from old platform (read-only)</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {resident.legacy_details.map((d) => <Row key={d.label} label={d.label} testId={`profile-legacy-${d.key}`}>{d.value}</Row>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileForm({ form, setForm, props }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = (k, label, type = "text") => (
    <div><label className={labelCls}>{label}</label>
      <input data-testid={`profile-input-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} /></div>
  );
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">{field("first_name", "First name")}{field("last_name", "Last name")}{field("preferred_name", "Preferred name")}</div>
      <div className="grid sm:grid-cols-3 gap-3">
        {field("intake_date", "Move-in / intake date", "date")}
        <div><label className={labelCls}>Status</label>
          <div data-testid="profile-status-readonly" className={`${inputCls} text-nex-muted`}>{pretty(form.status)} · change in SA Controls</div></div>
        <div><label className={labelCls}>New SSN (optional)</label>
          <input data-testid="profile-input-ssn" value={form.ssn} placeholder="9 digits" onChange={(e) => set("ssn", e.target.value)} className={inputCls} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {["name", "email"].map((k) => (
          <div key={k}><label className={labelCls}>Case manager {k}</label>
            <input data-testid={`profile-input-cm-${k}`} value={form.case_manager[k]} onChange={(e) => set("case_manager", { ...form.case_manager, [k]: e.target.value })} className={inputCls} /></div>
        ))}
      </div>
      <div><label className={labelCls}>Housing</label><HousingSelect properties={props} value={form.housing} onChange={(h) => set("housing", h)} /></div>
      <div className="grid sm:grid-cols-4 gap-2">
        {GOV.map(([k, l]) => (
          <div key={k}><label className={labelCls}>{l}</label>
            <select data-testid={`profile-input-gov-${k}`} value={form.gov[k]} onChange={(e) => set("gov", { ...form.gov, [k]: e.target.value })} className={inputCls}>
              {GOV_STATUS.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}</select></div>
        ))}
      </div>
    </div>
  );
}

export default function ProfileTab({ resident, onChanged }) {
  const sa = useAuth().user?.role === "super_admin";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => toForm(resident));
  const [details, setDetails] = useState(null);
  const [dv, setDv] = useState({});
  const [props, setProps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { reApi.get("/properties").then((d) => setProps(d.properties)).catch(() => {}); }, []);
  useEffect(() => { setForm(toForm(resident)); }, [resident]);
  useEffect(() => { if (sa) reApi.get(`/residents/${resident.id}/details`).then((d) => { setDetails(d); setDv(d.values); }).catch(() => {}); }, [sa, resident]);
  useEffect(() => {
    const f = (e) => { if (sa && e.detail?.id === resident.id) reApi.get(`/residents/${resident.id}/details`).then((d) => { setDetails(d); if (!editing) setDv(d.values); }).catch(() => {}); };
    window.addEventListener("accounts:login-pin-changed", f); return () => window.removeEventListener("accounts:login-pin-changed", f);
  }, [sa, resident.id, editing]);

  const p = props.find((x) => x.id === resident.housing?.property_id);
  const room = p?.rooms.find((r) => r.id === resident.housing?.room_id);
  const bed = room?.beds.find((b) => b.id === resident.housing?.bed_id);
  const housingLabel = [p?.name, room?.name, bed?.name].filter(Boolean).join(" · ");

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const h = form.housing?.property_id ? form.housing : null;
      await reApi.patch(`/residents/${resident.id}`, {
        first_name: form.first_name, last_name: form.last_name, preferred_name: form.preferred_name || null,
        status: form.status, intake_date: form.intake_date || null, case_manager: form.case_manager, housing: h,
        government_ids: { ...form.gov, ...(form.ssn ? { ssn: form.ssn } : {}) },
      });
      const diff = details ? Object.fromEntries(Object.entries(dv).filter(([k, v]) => (v ?? "") !== (details.values[k] ?? ""))) : {};
      if (Object.keys(diff).length) { const d = await reApi.put(`/residents/${resident.id}/details`, { values: diff }); setDetails(d); setDv(d.values); }
      setEditing(false); onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-tab" className="space-y-4">
      <div className="flex justify-end gap-2">
        {editing ? (<>
          <GhostButton data-testid="profile-cancel" onClick={() => { setEditing(false); setForm(toForm(resident)); setDv(details?.values || {}); setError(null); }}><X className="h-3 w-3" /> Cancel</GhostButton>
          <GoldButton data-testid="profile-save" onClick={save} disabled={saving}><Save className="h-3 w-3" /> Save</GoldButton>
        </>) : <GhostButton data-testid="profile-edit" onClick={() => setEditing(true)}><Pencil className="h-3 w-3" /> Edit profile</GhostButton>}
      </div>
      <ErrorLine testId="profile-error">{error}</ErrorLine>
      {editing ? <ProfileForm form={form} setForm={setForm} props={props} /> : <ProfileView resident={resident} housingLabel={housingLabel} sa={sa} />}
      {sa && <IntakeDetails data={details} values={editing ? dv : details?.values || {}} onChange={editing ? (k, v) => setDv((x) => ({ ...x, [k]: v })) : null} />}
    </div>
  );
}
